/**
 * Pre-roll (RW-4.43c): what the user says right after the wake phrase
 * reaches the call.
 *
 * "Hey Jarvis, what's on today?" — the call takes a second or two to
 * connect, and without this the question was lost: the chime was the cue to
 * start over. Now the microphone keeps recording from the moment the phrase
 * is heard, and that recording is the first thing the call's microphone
 * track plays; the track then catches up with the live microphone by
 * skipping silence, so the brain hears the whole question and the delay
 * shrinks to nothing within the first pause.
 *
 * Rejected: transcribing the captured audio and sending text (no speech
 * recognition on the device, and it would bypass the call's own STT and
 * turn detection); publishing the recording as a second track (the voice
 * worker listens to one microphone); a fixed delay on the live track (a
 * permanent 2 s lag on every turn).
 *
 * The queue is `preroll-fifo.ts`; `preroll.worklet.ts` runs it, and
 * `startPreroll` wires it to a stream.
 */
import workletUrl from "./preroll.worklet.ts?worker&url";
import type { PrerollPhase } from "./preroll-fifo";

export interface Preroll {
  /** The call's microphone: the recording first, then the live microphone. */
  readonly track: MediaStreamTrack;
  /** The wake fired: keep everything from now (and LOOKBACK_S before, `preroll-fifo.ts`). */
  capture(): void;
  /** The call published `track`: start playing. */
  release(): void;
  /** Stop the audio graph and the microphone it holds. */
  stop(): void;
}

/** Run a pre-roll on `stream`. It owns the stream from then on. */
export async function startPreroll(stream: MediaStream): Promise<Preroll> {
  const ctx = new AudioContext();
  try {
    await ctx.audioWorklet.addModule(workletUrl);
    const node = new AudioWorkletNode(ctx, "omni-preroll", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    const source = ctx.createMediaStreamSource(stream);
    const dest = ctx.createMediaStreamDestination();
    source.connect(node);
    node.connect(dest);
    const track = dest.stream.getAudioTracks()[0];
    let stopped = false;
    const send = (phase: PrerollPhase) => node.port.postMessage({ phase });
    return {
      track,
      capture: () => send("capturing"),
      release: () => send("live"),
      stop: () => {
        if (stopped) return;
        stopped = true;
        source.disconnect();
        node.disconnect();
        track.stop();
        stream.getTracks().forEach((t) => t.stop());
        void ctx.close();
      },
    };
  } catch (err) {
    void ctx.close();
    throw err;
  }
}
